// DashboardBuilder.js - Updated with Sub-tab support + Save to DB
import React, { useState, useMemo, useContext, useEffect, useCallback } from "react";
import { v4 as uuidv4 } from "uuid";
import { saveAs } from "file-saver";
import VariableContext from "../contexts/VariableContext";

import Sidebar from "../components/DashboardBuilder/Sidebar";
import TabNavigation from "../components/DashboardBuilder/TabNavigation";
import TabContent from "../components/DashboardBuilder/TabContent";
import GlobalFilters from "../components/DashboardBuilder/GlobalFilters";
import SubTabManager from "../components/DashboardBuilder/SubTabManager";
import "./styles/DashboardBuilder.css";
import generateChartData from "../utils/generateChartData";
import generateCrosstab from "../utils/generateCrosstab";
import config from "../configs/config";
import MultiResponse from "./MultiResponse";
import FetchingFreq from "../contexts/FetchingFreq";
import DashboardPreview from "../components/DashboardPreview/DashboardPreview";

// ─── Helper: create a blank tab node ───────────────────────────────────────
const createTab = (title = "New Tab") => ({
  id: uuidv4(),
  title,
  filters: [],
  kpis: [],
  Sub: [],
  showParentKpi: true,
  showMainWithSub: false,
});

// ─── Helper: deep-update a node anywhere in the tree ────────────────────────
const updateNodeById = (nodes, targetId, updater) =>
  nodes.map((node) => {
    if (node.id === targetId) return updater(node);
    return { ...node, Sub: updateNodeById(node.Sub || [], targetId, updater) };
  });

// ─── Helper: remove a node anywhere in the tree ─────────────────────────────
const removeNodeById = (nodes, targetId) =>
  nodes
    .filter((n) => n.id !== targetId)
    .map((n) => ({ ...n, Sub: removeNodeById(n.Sub || [], targetId) }));

// ─── Helper: find a node by id anywhere in the tree ─────────────────────────
const findNodeById = (nodes, targetId) => {
  for (const n of nodes) {
    if (n.id === targetId) return n;
    const found = findNodeById(n.Sub || [], targetId);
    if (found) return found;
  }
  return null;
};

// ─── Helper: add a sub-tab under a specific parent ──────────────────────────
const addSubUnderNode = (nodes, parentId, newSub) =>
  updateNodeById(nodes, parentId, (node) => ({
    ...node,
    Sub: [...(node.Sub || []), newSub],
  }));

const DashboardBuilder = () => {
  const { dataGroup, originalDataRows, projectId } = useContext(VariableContext);
  const { frequencyData } = useContext(FetchingFreq);

  const [tabs, setTabs] = useState([createTab("Overview")]);
  const [dataDashboard, setDataDashboard] = useState(null);
  const [selectedTabId, setSelectedTabId] = useState(tabs[0].id);
  const [globalFilters, setGlobalFilters] = useState([]);
  const [selectedQuestionKey, setSelectedQuestionKey] = useState(null);
  const [logoPath, setLogoPath] = useState("");
  const [showCrosstabModal, setShowCrosstabModal] = useState(false);
  const [crosstabGenerated, setCrosstabGenerated] = useState(false);
  const [crosstabDisplayData, setCrosstabDisplayData] = useState(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null); // 'success' | 'error' | null
  // ─── NEW: preview button state ───────────────────────────────────────────
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [previewStatus, setPreviewStatus] = useState(null); // 'error' | null

  const selectedTab = useMemo(
    () => findNodeById(tabs, selectedTabId),
    [tabs, selectedTabId]
  );

  const selectedVariable = useMemo(
    () => (selectedQuestionKey && dataGroup ? dataGroup[selectedQuestionKey] : null),
    [selectedQuestionKey, dataGroup]
  );

  // ─── Filter logic ───────────────────────────────────────────────────────
  const resolveVariableKey = (selectedVariable, questionKey, fallbackVariableName) => {
    if (!selectedVariable && !questionKey && !fallbackVariableName) return null;
    if (selectedVariable?.originalColumnName) return selectedVariable.originalColumnName;
    if (selectedVariable?.varName?.[0]?.Name) return selectedVariable.varName[0].Name;
    if (selectedVariable?.variableName) return selectedVariable.variableName;
    if (fallbackVariableName) return fallbackVariableName;
    if (questionKey) return questionKey;
    return null;
  };

  const applyAllFilters = useCallback(
    (rows, tab, globalFilters) => {
      if (!Array.isArray(rows)) return [];
      let filtered = [...rows];

      const normalize = (v) => {
        if (v === null || v === undefined) return "";
        return String(v).trim().toLowerCase();
      };

      const getResolvedColumnName = (filter) => {
        const variableKey =
          filter.variable_name || filter.variableName || filter.varName || filter.key;
        if (!variableKey) return null;
        const variableMeta = dataGroup?.[variableKey];
        return resolveVariableKey(variableMeta, variableKey);
      };

      const applyFilter = (rows, f) => {
        const colName = getResolvedColumnName(f);
        if (!colName) return rows;
        if (Array.isArray(f.selectedValue)) {
          if (f.selectedValue.length === 0) return rows;
          const normalizedSelected = f.selectedValue.map(normalize);
          return rows.filter((row) => normalizedSelected.includes(normalize(row[colName])));
        }
        const selected = normalize(f.selectedValue);
        if (!selected) return rows;
        return rows.filter((row) => normalize(row[colName]) === selected);
      };

      if (tab?.filters?.length) {
        tab.filters.forEach((f) => { filtered = applyFilter(filtered, f); });
      }

      if (globalFilters?.length) {
        globalFilters.forEach((f) => { filtered = applyFilter(filtered, f); });
      }

      return filtered;
    },
    [dataGroup]
  );

  const isNumericVariable = (variable) => {
    if (!variable) return false;
    const t = (variable.questionType || variable.type || variable.Qtype || "")
      .toString()
      .toLowerCase();
    return ["numeric", "number", "integer", "float"].includes(t);
  };

  const detectVariableType = (variable, key, rows) => {
    const variableKey = resolveVariableKey(variable, key);
    if (isNumericVariable(variable)) return "numeric";
    const allCols = Object.keys(rows?.[0] || {});
    const prefixMatch = variableKey?.match(/^([A-Za-z]+\d+)/);
    const prefix = prefixMatch ? prefixMatch[1] : variableKey;
    const gridCols = allCols.filter((c) => c.startsWith(prefix + "r"));
    if (gridCols.length > 1) return "multigrid";
    return "categorical";
  };

  // ─── KPI recalculation on filter change ────────────────────────────────
  useEffect(() => {
    if (!selectedTab || !originalDataRows || originalDataRows.length === 0) return;

    const filteredRows = applyAllFilters(originalDataRows, selectedTab, globalFilters);
    const totalBase = originalDataRows.length;

    setTabs((prev) =>
      updateNodeById(prev, selectedTabId, (tab) => {
        const updatedKPIs = tab.kpis.map((kpi) => {
          if (kpi.isCrosstab) {
            const rowKeys = kpi.rowVars || [];
            const colKeys = kpi.colVars || [];
            if (!rowKeys.length || !colKeys.length) return kpi;
            const rowMetas = rowKeys.map((k) => dataGroup[k] || {});
            const colMetas = colKeys.map((k) => dataGroup[k] || {});
            const newData = generateCrosstab(filteredRows, rowKeys, colKeys, rowMetas, colMetas);
            if (newData) return { ...kpi, data: newData };
          }
          return kpi;
        });
        return { ...tab, kpis: updatedKPIs };
      })
    );
  }, [selectedTabId, globalFilters, dataGroup, originalDataRows]);

  // ─── Add KPI ────────────────────────────────────────────────────────────
  const handleAddKPI = (variableKeys) => {
    if (!Array.isArray(variableKeys)) variableKeys = [variableKeys];
    if (variableKeys.length === 0) { alert("Select at least one variable."); return; }

    const types = variableKeys.map((key) => {
      const v = dataGroup[key];
      if (!v) return null;
      return detectVariableType(v, key, originalDataRows);
    });
    const uniqueTypes = [...new Set(types)];
    if (uniqueTypes.length > 1) { alert("You can only merge variables of the same type."); return; }

    const varType = uniqueTypes[0];
    const filteredRows = applyAllFilters(originalDataRows, selectedTab, globalFilters);
    const totalBase = originalDataRows.length;

    let combinedData = [];
    let combinedOptions = [];

    if (varType === "categorical") {
      variableKeys.forEach((key) => {
        const selectedVar = dataGroup[key];
        const variableKey = resolveVariableKey(selectedVar, key);
        let options = [];

        if (Array.isArray(selectedVar?.Rows) && selectedVar.Rows.length) {
          options = selectedVar.Rows.map((r) => {
            const m = r?.title?.match(/^([A-Za-z0-9_]+r\d+)(.*)$/);
            const value = r.index ?? r.value ?? r.Id ?? r.index;
            const label = m ? m[2].trim() : r?.title;
            return { value: String(value), label };
          });
        } else if (Array.isArray(selectedVar?.values) && selectedVar.values.length) {
          options = selectedVar.values.map((v) => ({
            value: String(v.index ?? v.Value ?? v.value),
            label: v?.title || v.label || String(v.index),
          }));
        }

        const originalData = generateChartData(originalDataRows, variableKey, { values: options });
        const originalBase = originalData[0]?.base || totalBase;
        const filteredData = generateChartData(filteredRows, variableKey, { values: options });

        filteredData.forEach((item) => {
          combinedData.push({
            name: item.name,
            value: item.value,
            percent: originalBase ? Number(((item.value / originalBase) * 100).toFixed(2)) : 0,
            base: originalBase,
            variable: key,
          });
        });
        combinedOptions.push(...options);
      });
    } else if (varType === "numeric") {
      variableKeys.forEach((key) => {
        const selectedVar = dataGroup[key];
        const variableKey = resolveVariableKey(selectedVar, key);
        const nums = filteredRows.map((r) => Number(r[variableKey])).filter((n) => Number.isFinite(n));
        const base = nums.length;
        const mean = base ? nums.reduce((a, b) => a + b, 0) / base : 0;
        const variance = base ? nums.reduce((acc, n) => acc + Math.pow(n - mean, 2), 0) / base : 0;
        combinedData.push({
          name: "Summary",
          count: nums.length,
          mean: Number(mean.toFixed(2)),
          stdDev: Number(Math.sqrt(variance).toFixed(2)),
          base,
          stackKey: key,
        });
      });
    }

    let qTitle = "No Question Title";
    for (const v of variableKeys) {
      const title = dataGroup?.[v]?.Qtitle?.trim();
      if (title && title !== "-") { qTitle = title; break; }
    }

    const varNames = variableKeys.map((key) => ({
      key,
      ...(dataGroup[key] ?? {}),
    }));

    const newKPI = {
      id: uuidv4(),
      label: variableKeys.join(", "),
      variableName: variableKeys.length === 1 ? variableKeys[0] : variableKeys,
      varNames,
      type: "Bar",
      data: combinedData,
      options: combinedOptions,
      viewMode: "chart",
      showTable: true,
      isNumeric: varType === "numeric" && variableKeys.length === 1,
      qTitle,
    };

    setTabs((prev) =>
      updateNodeById(prev, selectedTabId, (node) => ({
        ...node,
        kpis: [...node.kpis, newKPI],
      }))
    );
  };

  // ─── Tab management ─────────────────────────────────────────────────────
  const addTab = () => {
    const name = prompt("Enter new tab name:");
    if (!name) return;
    const newTab = createTab(name);
    setTabs((prev) => [...prev, newTab]);
    setSelectedTabId(newTab.id);
  };

  const renameTab = (tabId) => {
    const newName = prompt("Enter new tab name:");
    if (!newName) return;
    setTabs((prev) =>
      updateNodeById(prev, tabId, (node) => ({ ...node, title: newName }))
    );
  };

  const deleteTab = (tabId) => {
    if (!window.confirm("Delete this tab/sub-tab and all its content?")) return;
    setTabs((prev) => {
      const updated = removeNodeById(prev, tabId);
      if (selectedTabId === tabId) {
        const first = updated[0];
        if (first) setSelectedTabId(first.id);
      }
      return updated;
    });
  };

  // ─── Sub-tab management ──────────────────────────────────────────────────
  const addSubTab = (parentId) => {
    const name = prompt("Enter sub-tab name:");
    if (!name) return;
    const newSub = createTab(name);
    setTabs((prev) => addSubUnderNode(prev, parentId, newSub));
    setSelectedTabId(newSub.id);
  };

  const updateTabMeta = (tabId, field, value) => {
    setTabs((prev) =>
      updateNodeById(prev, tabId, (node) => ({ ...node, [field]: value }))
    );
  };

  // ─── KPI / Filter CRUD ──────────────────────────────────────────────────
  const updateKPI = (tabId, kpiId, field, value) => {
    setTabs((prev) =>
      updateNodeById(prev, tabId, (node) => ({
        ...node,
        kpis: node.kpis.map((kpi) =>
          kpi.id === kpiId ? { ...kpi, [field]: value } : kpi
        ),
      }))
    );
  };

  const removeKPI = (tabId, kpiId) => {
    setTabs((prev) =>
      updateNodeById(prev, tabId, (node) => ({
        ...node,
        kpis: node.kpis.filter((k) => k.id !== kpiId),
      }))
    );
  };

  const updateFilter = (tabId, filterId, selectedValue) => {
    setTabs((prev) =>
      updateNodeById(prev, tabId, (node) => ({
        ...node,
        filters: node.filters.map((f) =>
          f.id === filterId ? { ...f, selectedValue } : f
        ),
      }))
    );
  };

  const removeFilter = (tabId, filterId) => {
    setTabs((prev) =>
      updateNodeById(prev, tabId, (node) => ({
        ...node,
        filters: node.filters.filter((f) => f.id !== filterId),
      }))
    );
  };

  const updateGlobalFilter = (id, selectedValue) => {
    setGlobalFilters((prev) =>
      prev.map((f) => (f.id === id ? { ...f, selectedValue } : f))
    );
  };

  const removeGlobalFilter = (filterId) => {
    setGlobalFilters((prev) => prev.filter((f) => f.id !== filterId));
  };

  const toggleGlobalFilterMultiple = (filterId) => {
    setGlobalFilters((prev) =>
      prev.map((f) =>
        f.id === filterId
          ? { ...f, multiple: f.multiple === 1 ? 0 : 1, selectedValue: f.multiple === 1 ? "" : [] }
          : f
      )
    );
  };

  const updateFilterMultiple = (tabId, filterId) => {
    setTabs((prev) =>
      updateNodeById(prev, tabId, (node) => ({
        ...node,
        filters: node.filters.map((f) =>
          f.id === filterId
            ? { ...f, multiple: f.multiple === 1 ? 0 : 1, selectedValue: f.multiple === 1 ? "" : [] }
            : f
        ),
      }))
    );
  };

  const toggleKPIViewMode = (tabId, kpiId) => {
    setTabs((prev) =>
      updateNodeById(prev, tabId, (node) => ({
        ...node,
        kpis: node.kpis.map((k) =>
          k.id === kpiId
            ? { ...k, viewMode: k.viewMode === "chart" ? "table" : "chart" }
            : k
        ),
      }))
    );
  };

  const toggleShowTable = (tabId, kpiId) => {
    setTabs((prev) =>
      updateNodeById(prev, tabId, (node) => ({
        ...node,
        kpis: node.kpis.map((k) =>
          k.id === kpiId ? { ...k, showTable: !k.showTable } : k
        ),
      }))
    );
  };

  const handleAddGlobalFilter = () => {
    if (!selectedVariable || !selectedQuestionKey) {
      alert("Please select a variable first.");
      return;
    }

    if (globalFilters.some((f) => f.variable_name === selectedQuestionKey)) {
      alert("This variable is already added as a Global Filter.");
      return;
    }

    const getLabel = (variable, key) => {
      const title = variable?.title || variable.Qtitle || variable.label || variable.Name;
      if (typeof title === "object" && title !== null) {
        if (Array.isArray(title)) return title[0]?.Name || title[0] || key;
        return title.Name || title.name || title?.title || key;
      }
      return typeof title === "string" ? title : key;
    };

    const label = getLabel(selectedVariable, selectedQuestionKey);
    const variableName = resolveVariableKey(selectedVariable, selectedQuestionKey);

    const options =
      selectedVariable.values?.map((v, i) => {
        let name, value;
        if (typeof v === "string") { name = v; value = v; }
        else if (typeof v === "object" && v !== null) {
          name = v?.title || v.label || v.Name || v.name || `Option ${i + 1}`;
          value = v.value ?? v.Value ?? v.index ?? v.Id ?? v.code ?? v?.title ?? v.Name ?? `value_${i + 1}`;
        } else { name = `Option ${i + 1}`; value = `value_${i + 1}`; }
        return {
          Id: i + 1,
          Name: typeof name === "string" ? name : String(name),
          ParentId: v?.ParentId || [0],
          Selected: v?.Selected ?? false,
          SortOrder: i + 1,
          Value: typeof value === "string" ? value : String(value),
          Visible: v?.Visible ?? false,
        };
      }) || [];

    const existingIds = globalFilters.map((f) => Number(f.id)).filter((n) => !isNaN(n));
    const nextId = existingIds.length > 0 ? Math.max(...existingIds) + 1 : 1;

    const newGlobalFilter = {
      id: nextId,
      global: 1,
      parent_id: null,
      label,
      variable_name: variableName,
      sort_order: globalFilters.length + 1,
      Selected: null,
      multiple: 1,
      visible: true,
      hide_on_tab: false,
      positioning: 0,
      RoleId: null,
      options,
      selectedValue: [],
      varNames: {
        key: selectedQuestionKey,
        ...(dataGroup[selectedQuestionKey] ?? {}),
      },
    };

    setGlobalFilters((prev) => [...prev, newGlobalFilter]);
  };

  const handleAddFilter = () => {
    if (!selectedVariable || !selectedTab) {
      alert("Please select a variable and a tab first.");
      return;
    }

    const getLabel = (variable, key) => {
      const title = variable?.title || variable.Qtitle || variable.label || variable.Name;
      if (typeof title === "object" && title !== null) {
        if (Array.isArray(title)) return title[0]?.Name || title[0] || key;
        return title.Name || title.name || title?.title || key;
      }
      return typeof title === "string" ? title : key;
    };

    const label = getLabel(selectedVariable, selectedQuestionKey);
    const variableName = resolveVariableKey(selectedVariable, selectedQuestionKey);

    const options =
      selectedVariable.values?.map((v, i) => {
        let name, value;
        if (typeof v === "string") { name = v; value = v; }
        else if (typeof v === "object" && v !== null) {
          name = v?.title || v.label || v.Name || v.name || `Option ${i + 1}`;
          value = v.value ?? v.Value ?? v.index ?? v.Id ?? v.code ?? v?.title ?? v.Name ?? `value_${i + 1}`;
        } else { name = `Option ${i + 1}`; value = `value_${i + 1}`; }
        return {
          Id: i + 1,
          Name: typeof name === "string" ? name : String(name),
          ParentId: v?.ParentId || [0],
          Selected: v?.Selected ?? false,
          SortOrder: i + 1,
          Value: typeof value === "string" ? value : String(value),
          Visible: v?.Visible ?? false,
        };
      }) || [];

    const existingIds = selectedTab.filters.map((f) => Number(f.id)).filter((n) => !isNaN(n));
    const nextId = existingIds.length > 0 ? Math.max(...existingIds) + 1 : 1;

    const newFilter = {
      id: nextId,
      global: 0,
      parent_id: null,
      label,
      variable_name: variableName,
      sort_order: selectedTab.filters.length + 1,
      Selected: null,
      multiple: 1,
      visible: true,
      hide_on_tab: false,
      positioning: 0,
      RoleId: null,
      options,
      selectedValue: [],
      varNames: {
        key: selectedQuestionKey,
        ...(dataGroup[selectedQuestionKey] ?? {}),
      },
    };

    setTabs((prev) =>
      updateNodeById(prev, selectedTabId, (node) => ({
        ...node,
        filters: [...node.filters, newFilter],
      }))
    );
  };

  // ─── Payload (shared by save + preview) ─────────────────────────────────
  const payload = {
    meta: { logo: logoPath, tabView: "dropdown" },
    layout: { sidebar: true, topNavbar: true },
    tabs,
    globalFilters,
    selectedTabId,
  };

  const saveDashboardToJson = () => {
    const blob = new Blob([JSON.stringify(payload, null, 2)], {
      type: "application/json;charset=utf-8",
    });
    saveAs(blob, "dashboard-config.json");
  };

  const saveDashboardToDB = async () => {
    if (!projectId) { alert("No projectId found. Cannot save."); return; }
    setIsSaving(true);
    setSaveStatus(null);

    try {
      const response = await fetch(`${config.API_BASE_URL}/dashboard/save`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ projectId, payload }),
      });

      if (!response.ok) {
        const text = await response.text();
        throw new Error(`HTTP ${response.status}: ${text}`);
      }

      setSaveStatus("success");
      setTimeout(() => setSaveStatus(null), 3000);
    } catch (err) {
      console.error("Save failed:", err);
      setSaveStatus("error");
      setTimeout(() => setSaveStatus(null), 3000);
    } finally {
      setIsSaving(false);
    }
  };

  useEffect(() => {
  const loadDashboard = async () => {
    if (!projectId) return;

    try {
      const res = await fetch(`${config.API_BASE_URL}/dashboard/load?projectId=${projectId}`);
      if (!res.ok) throw new Error("Failed to load dashboard");

      const data = await res.json();
      console.log("Loaded dashboard data:", data);

      if (data?.payload) {
        const { tabs, globalFilters, selectedTabId, meta } = data.payload;

        setTabs(tabs || [createTab("Overview")]);
        setGlobalFilters(globalFilters || []);
        setSelectedTabId(selectedTabId || tabs?.[0]?.id);
        setLogoPath(meta?.logo || "");
      }
    } catch (err) {
      console.error("Load dashboard failed:", err);
    }
  };

  loadDashboard();
}, [projectId]);

  // ─── Preview Dashboard ───────────────────────────────────────────────────
  // Flow:
  //   1. Save the current dashboard config to DB (so preview sees latest state).
  //   2. Warm up the frequency cache on the backend.
  //   3. Open the DashboardPage in a new tab — it will call /dashboard/load
  //      itself on mount, exactly as a real user would see it.
  const handlePreview = async () => {
    if (!projectId) {
      alert("No projectId found. Cannot preview.");
      return;
    }

    setIsPreviewing(true);
    setPreviewStatus(null);

    try {
      // Step 1: Save latest dashboard config to DB
      const saveRes = await fetch(`${config.API_BASE_URL}/dashboard/save`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ projectId, payload }),
      });

      if (!saveRes.ok) {
        const text = await saveRes.text();
        throw new Error(`Save failed — HTTP ${saveRes.status}: ${text}`);
      }

      // Step 2: Warm up frequency cache (fire-and-forget; non-blocking)
      //         Errors here are intentionally swallowed — the preview can still
      //         open and will fetch on demand via DynamicTabs.
      fetch(`${config.API_BASE_URL}/Dashboardfrequency?projectId=${projectId}`).catch(
        () => {}
      );

      // Step 3: Open DashboardPage in a new tab
      //         Uses window.location.origin so it works in any environment
      //         (dev on any port, staging, production) without hardcoding.
      const previewUrl = `https://dashboardpreview.excavateservices.com/project/${projectId}/dashboard`;
      window.open(previewUrl, "_blank");
    } catch (err) {
      console.error("Preview failed:", err);
      setPreviewStatus("error");
      setTimeout(() => setPreviewStatus(null), 4000);
    } finally {
      setIsPreviewing(false);
    }
  };

  return (
    <div className="dashboard-wrapper">
      <Sidebar
        dataGroup={dataGroup}
        selectedVariable={selectedVariable}
        selectedQuestionKey={selectedQuestionKey}
        setSelectedQuestionKey={setSelectedQuestionKey}
        handleAddKPI={handleAddKPI}
        handleAddFilter={handleAddFilter}
        handleAddGlobalFilter={handleAddGlobalFilter}
        globalFilters={globalFilters}
        selectedTab={selectedTab}
        showCrosstabModal={showCrosstabModal}
        setShowCrosstabModal={setShowCrosstabModal}
        crosstabDisplayData={crosstabDisplayData}
        setCrosstabDisplayData={setCrosstabDisplayData}
      />

      <div className="main-content" style={{ overflow: "hidden" }}>
        {dataDashboard && (
          <MultiResponse
            varProperties={dataDashboard}
            displaySettings={{ showFrequency: true, showPercent: true }}
          />
        )}

        <GlobalFilters
          globalFilters={globalFilters}
          setGlobalFilters={setGlobalFilters}
          updateGlobalFilter={updateGlobalFilter}
          toggleGlobalFilterMultiple={toggleGlobalFilterMultiple}
          variables={dataGroup}
          tabs={tabs}
          removeGlobalFilter={removeGlobalFilter}
        />

        {/* ── Top-level tabs ── */}
        <TabNavigation
          tabs={tabs}
          selectedTabId={selectedTabId}
          setSelectedTabId={setSelectedTabId}
          addTab={addTab}
          renameTab={renameTab}
        />

        {isPreviewOpen && (
          <DashboardPreview payload={payload} onClose={() => setIsPreviewOpen(false)} />
        )}

        {/* ── Logo + action buttons ── */}
        <div style={{ margin: "10px 0", display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
          <label style={{ fontWeight: "bold" }}>Logo Path:</label>
          <input
            type="text"
            placeholder="Enter logo URL or path..."
            value={logoPath}
            onChange={(e) => setLogoPath(e.target.value)}
            style={{ width: "280px" }}
          />

          <button className="download-json-btn" onClick={saveDashboardToJson}>
            ⬇ Download JSON
          </button>

          {/* ── Save to DB ── */}
          <button
            className="save-db-btn"
            onClick={saveDashboardToDB}
            disabled={isSaving}
            style={{
              background:
                isSaving
                  ? "#94a3b8"
                  : saveStatus === "success"
                  ? "#16a34a"
                  : saveStatus === "error"
                  ? "#dc2626"
                  : "#2563eb",
              color: "#fff",
              border: "none",
              padding: "6px 16px",
              borderRadius: "6px",
              cursor: isSaving ? "not-allowed" : "pointer",
              fontWeight: 600,
              fontSize: "13px",
              transition: "background 0.2s",
            }}
          >
            {isSaving
              ? "⏳ Saving..."
              : saveStatus === "success"
              ? "✅ Saved!"
              : saveStatus === "error"
              ? "❌ Error"
              : "💾 Save Dashboard"}
          </button>

          {/* ── Preview Dashboard ── */}
          <button
            className="preview-btn"
            onClick={handlePreview}
            disabled={isPreviewing}
            style={{
              background:
                isPreviewing
                  ? "#94a3b8"
                  : previewStatus === "error"
                  ? "#dc2626"
                  : "#7c3aed",
              color: "#fff",
              border: "none",
              padding: "6px 16px",
              borderRadius: "6px",
              cursor: isPreviewing ? "not-allowed" : "pointer",
              fontWeight: 600,
              fontSize: "13px",
              transition: "background 0.2s",
            }}
          >
            {isPreviewing
              ? "⏳ Opening..."
              : previewStatus === "error"
              ? "❌ Preview Failed"
              : "👁 Preview Dashboard"}
          </button>
        </div>

        {/* ── Sub-tab tree for currently selected top-level tab ── */}
        {selectedTab && (
          <SubTabManager
            tabs={tabs}
            selectedTabId={selectedTabId}
            setSelectedTabId={setSelectedTabId}
            addSubTab={addSubTab}
            renameTab={renameTab}
            deleteTab={deleteTab}
            updateTabMeta={updateTabMeta}
          />
        )}

        <div className="tab-content-area">
          <TabContent
            selectedTab={selectedTab}
            filteredRows={applyAllFilters(originalDataRows, selectedTab, globalFilters)}
            updateKPI={updateKPI}
            updateFilter={updateFilter}
            updateFilterMultiple={updateFilterMultiple}
            globalFilters={globalFilters}
            removeKPI={removeKPI}
            removeFilter={removeFilter}
            toggleKPIViewMode={toggleKPIViewMode}
            toggleShowTable={toggleShowTable}
            crosstabDisplayData={crosstabDisplayData}
            setCrosstabDisplayData={setCrosstabDisplayData}
          />
        </div>
      </div>
    </div>
  );
};

export default DashboardBuilder;